// exclusiveViewerStyle.js
'use strict';
import { css } from 'styled-components';

import media from '../style/media.js';
import { EMPTY_OBJECT } from '../resource/defaults.js';
import { color } from '../style/variables.js';

export const EXCLUSIVE_VIEWER_SIZE_TABLET = { width: 124, height: 220 };
export const EXCLUSIVE_VIEWER_SIZE_DESKTOP = { width: 240, height: 135 };
// Desktop keeps the video ratio inside this bounding box.
const EXCLUSIVE_VIEWER_BOUNDING_BOX_DESKTOP = { width: 240, height: 220 };

// EXCLUSIVE_VIEWER window absolute position setting
export const EXCLUSIVE_VIEWER_RIGHT = 8;
export const EXCLUSIVE_VIEWER_BOTTOM_WITH_TOP_GIFT = 102;
export const EXCLUSIVE_VIEWER_BOTTOM_WITHOUT_TOP_GIFT = 80;
const EXCLUSIVE_VIEWER_TOP = 60;
export const EXCLUSIVE_VIEWER_FLOAT_Z_INDEX = 4;

// Window look (radius / shadow), single source. Shared by the self window
// and the shrunk streamer player so both small windows always look the same.
export const exclusiveViewerWindowVisualStyle = css`
  border-radius: 4px;
  box-shadow: 0 6px 22px ${color.neutral['0_alpha_80']};
`;

// Tablet and below keep one fixed tall frame whatever the stream orientation
// is, so the chat keeps its reserved width. Desktop fits the ratio into the
// bounding box, driven by --pip-ratio from getPipWindowStyle; the value below
// is the fallback used until a valid video size arrives.
export const exclusiveViewerWindowSizeStyle = css`
  --pip-ratio: ${EXCLUSIVE_VIEWER_SIZE_DESKTOP.width /
  EXCLUSIVE_VIEWER_SIZE_DESKTOP.height};
  --pip-width: min(
    ${EXCLUSIVE_VIEWER_BOUNDING_BOX_DESKTOP.width}px,
    ${EXCLUSIVE_VIEWER_BOUNDING_BOX_DESKTOP.height}px * var(--pip-ratio)
  );
  width: var(--pip-width);
  height: calc(var(--pip-width) / var(--pip-ratio));
  ${media.tablet`
    width: ${EXCLUSIVE_VIEWER_SIZE_TABLET.width}px;
    height: ${EXCLUSIVE_VIEWER_SIZE_TABLET.height}px;
  `}
`;

// Interact with LiveStream.jsx child components layout setting
export const EXCLUSIVE_VIEWER_WIDTH = EXCLUSIVE_VIEWER_SIZE_TABLET.width;
export const EXCLUSIVE_VIEWER_GAP = 8;
export const EXCLUSIVE_VIEWER_RESERVED_WIDTH =
  EXCLUSIVE_VIEWER_WIDTH + EXCLUSIVE_VIEWER_GAP + EXCLUSIVE_VIEWER_RIGHT;

// Interact with PaidInteractionPanel.jsx (topGift) layout setting
export const getExclusiveViewerWindowBottom = ({ hasTopGift }) =>
  hasTopGift
    ? EXCLUSIVE_VIEWER_BOTTOM_WITH_TOP_GIFT
    : EXCLUSIVE_VIEWER_BOTTOM_WITHOUT_TOP_GIFT;

/**
 * Build the exclusive viewer camera floating window's config for useDraggableFloatingWindow.
 * Only edge margins (size comes from CSS); returns a new object — wrap in useMemo([hasTopGift]).
 * @param {object} params
 * @param {boolean} params.hasTopGift - whether this session has topGift.
 * @returns {object} { margins } for useDraggableFloatingWindow.
 * @example
 * const config = useMemo(() => getExclusiveViewerFloatingConfig({ hasTopGift }), [hasTopGift]);
 */
export const getExclusiveViewerFloatingConfig = ({ hasTopGift }) => ({
  margins: {
    horizontal: EXCLUSIVE_VIEWER_RIGHT,
    top: EXCLUSIVE_VIEWER_TOP,
    bottom: getExclusiveViewerWindowBottom({ hasTopGift }),
  },
});

/**
 * Turn a video's intrinsic size into the custom properties that drive
 * exclusiveViewerWindowSizeStyle. An unusable size returns nothing so the
 * stylesheet fallback stays in effect.
 * @param {object} params
 * @param {number} [params.videoWidth] - HTML video intrinsic width.
 * @param {number} [params.videoHeight] - HTML video intrinsic height.
 * @returns {object} Inline style carrying --pip-ratio.
 * @example
 * getPipWindowStyle({ videoWidth: 1920, videoHeight: 1080 });
 * // => { '--pip-ratio': '1.7777777777777777' }
 */
export const getPipWindowStyle = ({ videoWidth, videoHeight }) => {
  // A local check keeps this file out of the video listener module; NaN and
  // Infinity would produce a calc() the browser drops.
  if (
    !Number.isFinite(videoWidth / videoHeight) ||
    videoWidth <= 0 ||
    videoHeight <= 0
  ) {
    return EMPTY_OBJECT;
  }
  return { '--pip-ratio': `${videoWidth / videoHeight}` };
};
